import React, { useState, useRef, ReactNode } from 'react';
import styled from 'styled-components';

import ErrorPanel from 'components/basic/panels/ErrorPanel';

const Wrapper = styled.div<{
  $isValid: boolean;
  $noMinWidth: boolean;
  disabled: boolean;
}>`
  width: 100%;
  ${({ $noMinWidth }) => ($noMinWidth ? '' : 'min-width: 150px;')}
  border: ${props =>
    props.$isValid ? '1px solid transparent' : '1px solid var(--red3);'};
  background: ${({ disabled }) =>
    disabled ? 'var(--mainDarkDisabled)' : 'var(--mainDark)'};
  border-radius: 2px;
  display: flex;
  justify-content: space-between;
  padding-right: 1px;
`;

const Input = styled.input<{ $isRightAlign: boolean }>`
  width: 100%;
  height: 40px;

  text-align: ${({ $isRightAlign }) => ($isRightAlign ? 'right' : 'initial')};

  border: none;
  padding: 0 10px 0 14px;
  color: white;
  background: none;

  ${({ inputMode }) =>
    inputMode === 'decimal' ? 'font-family: var(--digitsFontFamily)' : ''};

  outline: none;

  transition: 0.5s background-color ease;

  &:active,
  &:focus {
    /* background: rgba(150, 150, 150, 0.04); */
    /* border: 1px solid #ffffff33; */
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

const LabelBox = styled.div<{ $paddingRight: boolean; $paddingLeft: boolean }>`
  display: flex;
  align-items: center;
  height: 40px;
  color: var(--gray4d);
  word-break: keep-all;
  ${({ $paddingRight }) => ($paddingRight ? 'padding-right: 8px;' : '')}
  ${({ $paddingLeft }) => ($paddingLeft ? 'padding-left: 8px;' : '')}
`;

const LeftLabelBox = styled(LabelBox)`
  white-space: nowrap;
`;

type Props = {
  /** Label on the right side of input */
  label: ReactNode;
  /** Label on the left side of input */
  labelOnLeft?: ReactNode;
  value: string | number;
  title?: string;
  onChange: (value: string) => void;
  type?: 'text' | 'password' | 'email';
  /** Numeric keyboard on mobile devices (inputmode="decimal") */
  numbericInputMode?: boolean;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  pattern?: string;
  rightAlign?: boolean;
  autoFocus?: boolean;
  error?: string;
  noMinWidth?: boolean;
  onFocusIn?: () => void;
  onFocusOut?: () => void;
};

export const decimalNumberPattern = '\\d{0,20}(\\.\\d{1,8})?';

const TextInputLabeled = ({
  label,
  labelOnLeft,
  value,
  // title,
  onChange,
  // maxLength,
  disabled = false,
  required = false,
  placeholder = undefined,
  pattern,
  type = 'text',
  numbericInputMode = false,
  rightAlign = false,
  autoFocus = false,
  noMinWidth = false,
  error,
  onFocusOut,
  onFocusIn,
}: Props) => {
  const [blured, setBlured] = useState<boolean | null>(null);
  const [valid, setValid] = useState<boolean>(true);
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <Wrapper
        $isValid={error ? false : valid}
        $noMinWidth={!!noMinWidth}
        disabled={disabled}
      >
        {labelOnLeft && (
          <LeftLabelBox
            $paddingRight
            $paddingLeft
            onClick={() => inputRef.current?.focus()}
          >
            {labelOnLeft}
          </LeftLabelBox>
        )}
        <div style={{ width: '100%' }}>
          <Input
            ref={inputRef}
            data-blured={blured}
            data-lpignore={true}
            className="unstyled"
            // displayErrors={blured === true}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            // title={title}
            // maxLength={maxLength}
            inputMode={numbericInputMode ? 'decimal' : undefined}
            required={required}
            pattern={pattern} /* HTML5 Validation pattern */
            onChange={event => {
              onChange(event.target.value);
              if (!valid) {
                setValid(true);
              }
            }}
            onFocus={() => {
              onFocusIn?.();
              if (blured) {
                setBlured(false);
              }
            }}
            onBlur={() => {
              onFocusOut?.();
              // Make sure there are not trailing spaces when going out of input
              const originalValue = `${value}`;
              const trimmedValue = originalValue.trim();
              if (originalValue !== trimmedValue) {
                onChange(trimmedValue);
              }

              setBlured(true);

              if (inputRef?.current?.checkValidity) {
                const isValid = inputRef.current.checkValidity();
                setValid(isValid);
              }
            }}
            value={value}
            $isRightAlign={rightAlign}
            autoFocus={autoFocus}
          />
        </div>
        <LabelBox
          // Add extra padding for basic text, Dropdown has it's own padding
          $paddingRight={typeof label === 'string'}
          $paddingLeft={false}
          onClick={
            typeof label === 'string'
              ? () => inputRef.current?.focus()
              : undefined
          }
        >
          {label}
        </LabelBox>
      </Wrapper>
      {error && (
        <ErrorPanel
          noBackground
          disableWarningIcon
          marginTop={0}
          message={error}
        />
      )}
    </>
  );
};

export default TextInputLabeled;
